import { DEFAULT_UNIFIED_ICON } from '../config/unifiedIcons';
import { MarkerShape } from '../types/category';
import mapboxgl from 'mapbox-gl';
import { darkenColor } from './colorUtils';

/**
 * Atténue une couleur hex en réduisant son intensité
 * @param color Couleur au format hex (#RRGGBB)
 * @param percent Pourcentage d'atténuation (0-100)
 * @returns Couleur atténuée
 */
export const softendColor = (color: string, percent: number = 15): string => {
  // Si la couleur n'est pas au format hex, retourner telle quelle
  if (!color || !color.startsWith('#')) {
    return color;
  }
  
  try {
    // Convertir hex en RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    // Atténuer en ajoutant du blanc (en augmentant les valeurs)
    const factor = percent / 100;
    const newR = Math.min(255, r + Math.round((255 - r) * factor));
    const newG = Math.min(255, g + Math.round((255 - g) * factor));
    const newB = Math.min(255, b + Math.round((255 - b) * factor));
    
    // Convertir en hex et retourner
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  } catch (error) {
    console.error('Erreur lors de l\'atténuation de la couleur:', error);
    return color; // En cas d'erreur, retourner la couleur d'origine
  }
};

/**
 * Interface pour les informations utilisateur nécessaires à l'affichage de l'avatar
 */
export interface UserAvatarInfo {
  id: string;
  initials?: string;
  color?: string;
  photo_url?: string | null;
}

/**
 * Crée un élément DOM personnalisé pour un marqueur sur la carte
 * @param color - Couleur de fond du marqueur
 * @param iconName - Nom de l'icône Font Awesome à utiliser
 * @param userInfo - Informations sur l'utilisateur propriétaire du marqueur (optionnel)
 * @param showUserAvatar - Indique si l'avatar de l'utilisateur doit être affiché
 * @returns Element HTML personnalisé pour le marqueur
 */
/**
 * Anime un marqueur sur la carte avec un effet de rebond et de grossissement
 * @param map - Instance de la carte Mapbox
 * @param markerId - ID du marqueur à animer
 */
export const animateMarker = (map: mapboxgl.Map, markerId: string): void => {
  // Récupérer l'élément DOM du marqueur
  const markerElement = document.querySelector(`.mapboxgl-marker[data-marker-id="${markerId}"]`) as HTMLElement;
  if (!markerElement) {
    return;
  }
  
  // Ajouter une classe pour l'animation
  markerElement.classList.add('marker-animated');
  
  // Mettre en avant le marqueur en augmentant son z-index
  const originalZIndex = markerElement.style.zIndex;
  markerElement.style.zIndex = '1000';
  
  // Définir l'animation CSS (effet très réduit)
  const keyframes = [
    { transform: 'scale(1) translateY(0)', filter: 'brightness(100%)', offset: 0 },
    { transform: 'scale(1.1) translateY(-3px)', filter: 'brightness(110%) drop-shadow(0 0 2px rgba(255,255,255,0.3))', offset: 0.3 },
    { transform: 'scale(1.05) translateY(0)', filter: 'brightness(105%) drop-shadow(0 0 1px rgba(255,255,255,0.2))', offset: 0.5 },
    { transform: 'scale(1.1) translateY(-2px)', filter: 'brightness(110%) drop-shadow(0 0 2px rgba(255,255,255,0.3))', offset: 0.7 },
    { transform: 'scale(1) translateY(0)', filter: 'brightness(100%)', offset: 1 }
  ];
  
  // Configurer l'animation avec une durée plus longue
  const options = {
    duration: 1500, // Animation plus lente
    iterations: 1,
    easing: 'ease-out' // Animation plus douce
  };
  
  // Lancer l'animation
  markerElement.animate(keyframes, options).onfinish = () => {
    markerElement.classList.remove('marker-animated');
    markerElement.style.zIndex = originalZIndex;
  };
};

/**
 * Crée un élément DOM personnalisé pour un marqueur sur la carte
 */
export const createCustomMarkerElement = (
  color: string, 
  iconName: string, 
  userInfo?: UserAvatarInfo, 
  showUserAvatar: boolean = false,
  markerId?: string,
  markerShape: MarkerShape = 'circle'
): HTMLDivElement => {

  // Créer le conteneur principal du marqueur
  const markerElement = document.createElement('div');
  markerElement.className = 'custom-marker';
  markerElement.style.position = 'relative';
  markerElement.style.transform = 'translate(-50%, -50%)';
  markerElement.style.zIndex = '1000'; // Z-index élevé pour garantir la visibilité
  markerElement.style.pointerEvents = 'auto'; // Assurer que les événements de souris fonctionnent
  
  // Ajouter l'attribut data-marker-id si fourni
  if (markerId) {
    markerElement.setAttribute('data-marker-id', markerId);
  }
  
  // Créer l'icône selon le type de marker
  const iconElement = document.createElement('i');
  iconElement.style.lineHeight = '1';
  iconElement.style.margin = '0';
  iconElement.style.padding = '0';
  
  if (markerShape === 'circle') {
    // Mode cercle : cercle coloré avec icône personnalisable
    const circleElement = document.createElement('div');
    circleElement.style.backgroundColor = darkenColor(color) || '#2c3e50';
    circleElement.style.width = '26px';
    circleElement.style.height = '26px';
    circleElement.style.borderRadius = '50%';
    circleElement.style.display = 'flex';
    circleElement.style.alignItems = 'center';
    circleElement.style.justifyContent = 'center';
    circleElement.style.cursor = 'pointer';
    circleElement.style.position = 'absolute';
    circleElement.style.left = '50%';
    circleElement.style.top = '50%';
    circleElement.style.transform = 'translate(-50%, -50%)';
    circleElement.style.boxShadow = '0 0 0 2px white, 0 0 5px rgba(0,0,0,0.5)';
    circleElement.style.zIndex = '999';
    
    iconElement.className = `fas fa-${iconName || DEFAULT_UNIFIED_ICON.faName}`;
    iconElement.style.color = '#ffffff';
    iconElement.style.fontSize = '14px';
    
    circleElement.appendChild(iconElement);
    markerElement.appendChild(circleElement);
  } else {
    // Mode icône directe : icône FontAwesome colorée sans cercle
    let faIcon = '';
    let iconSize = '22px'; // Taille par défaut pour house, building, person
    
    switch (markerShape) {
      case 'house':
        faIcon = 'home';
        break;
      case 'building':
        faIcon = 'building';
        break;
      case 'person':
        faIcon = 'user';
        break;
      case 'marker':
        faIcon = 'map-marker-alt';
        iconSize = '29px'; // Taille augmentée uniquement pour le marker
        break;
      default:
        faIcon = iconName || DEFAULT_UNIFIED_ICON.faName;
    }
    
    iconElement.className = `fas fa-${faIcon}`;
    iconElement.style.color = color || '#2c3e50';
    iconElement.style.fontSize = iconSize;
    iconElement.style.cursor = 'pointer';
    iconElement.style.position = 'absolute';
    iconElement.style.left = '50%';
    iconElement.style.top = '50%';
    iconElement.style.transform = 'translate(-50%, -50%)';
    // Contour blanc similaire au cercle : 2px de blanc + ombre
    iconElement.style.filter = 'drop-shadow(0 0 0 white) drop-shadow(0 1px 0 white) drop-shadow(1px 0 0 white) drop-shadow(-1px 0 0 white) drop-shadow(0 -1px 0 white) drop-shadow(0 0 5px rgba(0,0,0,0.5))';
    iconElement.style.zIndex = '999';
    
    markerElement.appendChild(iconElement);
  }
  
  // Ajouter l'avatar de l'utilisateur si demandé et si les informations sont disponibles
  if (showUserAvatar && userInfo) {
    
    // Créer le cercle de l'avatar en bas à gauche du marqueur
    const avatarElement = document.createElement('div');
    const _markerSize = 26; // Taille approximative du marqueur principal (réduite de 30 à 26)
    const avatarSize = 22; // Taille de l'avatar ajustée en proportion
    
    avatarElement.style.width = `${avatarSize}px`;
    avatarElement.style.height = `${avatarSize}px`;
    avatarElement.style.borderRadius = '50%';
    avatarElement.style.position = 'absolute';
    
    // Positionner l'avatar en bas à gauche avec un chevauchement très important
    // Le positionnement est calculé pour que l'avatar chevauche une grande partie du marqueur
    avatarElement.style.bottom = '10px'; 
    avatarElement.style.left = '10px';
    avatarElement.style.transform = 'translate(-30%, 30%)'
    
    avatarElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
    avatarElement.style.zIndex = '5'; // Z-index entre le marqueur et son contenu
    
    // Définir l'arrière-plan de l'avatar (couleur ou image)
    if (userInfo.photo_url) {
      avatarElement.style.backgroundImage = `url(${userInfo.photo_url})`;
      avatarElement.style.backgroundSize = 'cover';
      avatarElement.style.backgroundPosition = 'center';
      // Pour les photos, utiliser une couleur atténuée pour la bordure
      avatarElement.style.border = `2px solid ${softendColor('#4285F4')}`;
      // Ajouter une fine bordure noire externe plus légère
      avatarElement.style.outline = '1px solid rgba(0,0,0,0.3)';
    } else {
      // Utiliser la couleur de l'utilisateur comme arrière-plan
      const avatarColor = userInfo.color || '#4285F4';
      // Atténuer la couleur de 15%
      const softenedColor = softendColor(avatarColor);
      avatarElement.style.backgroundColor = softenedColor;
      // Utiliser la même couleur atténuée pour la bordure
      avatarElement.style.border = `2px solid ${softenedColor}`;
      // Ajouter une fine bordure noire externe plus légère
      avatarElement.style.outline = '1px solid rgba(0,0,0,0.3)';
      avatarElement.style.display = 'flex';
      avatarElement.style.alignItems = 'center';
      avatarElement.style.justifyContent = 'center';
      avatarElement.style.color = 'white';
      avatarElement.style.fontSize = '10px';
      avatarElement.style.fontWeight = 'bold';
      
      // Ajouter les initiales si disponibles
      if (userInfo.initials) {
        avatarElement.textContent = userInfo.initials;
      }
    }
    
    markerElement.appendChild(avatarElement);
  }
  
  return markerElement;
};

